<template>
  <ul
    class="deep-links"
    :class="allClasses"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <li
      v-for="link in links"
      :key="`${link.url}-${link.text}`"
      class="deep-links__item"
      :data-test-id="`deep-links-item-${link.url}`"
    >
      <ELinkBubble
        :href="link.url"
        :target="linkTarget"
        :title="link.text"
        at-breakpoint="mobile-tablet"
        color="result"
        size="s"
        class="deep-links__link"
        data-test-id="deep-links-link"
        rel="noopener"
        @click="onLinkClick(link)"
      >
        <span class="deep-links__link-text">
          {{ link.text }}
        </span>
      </ELinkBubble>
      <p
        v-if="link.description"
        class="deep-links__description"
        data-test-id="deep-links-description"
      >
        {{ link.description }}
      </p>
      <p
        v-if="link.descriptionOne"
        class="deep-links__description"
        data-test-id="deep-links-description"
      >
        {{ link.descriptionOne }}
        <br v-if="link.descriptionTwo">
        {{ link.descriptionTwo }}
      </p>
    </li>
  </ul>
</template>

<script>
import { mapGetters } from 'vuex';

import classes, { getModifierClass } from '~~/common/vue/mixins/classes';
import ELinkBubble from '~~/common/vue/components/link-bubble';
import { trackClick, TYPE_DEEP_LINK } from '~~/search/common/js/client/analytics/results';

const linkProperties = ['url', 'text'];
const validateLink = (link) => linkProperties.every((property) => !!link[property]);

export const types = ['ad', 'web'];

export default {
  name: 'EDeepLinks',
  components: {
    ELinkBubble,
  },
  mixins: [classes],
  props: {
    links: {
      type: Array,
      required: true,
      validator: (value) => (Array.isArray(value) && value.every(validateLink)),
    },
    type: {
      type: String,
      validator: (value) => types.includes(value),
      required: true,
      cssClass: true,
    },
    rank: {
      type: [Number, null],
      default: null,
    },
    pagePosition: {
      type: [String, null],
      default: null,
    },
  },
  computed: {
    ...mapGetters('user', ['linkTarget']),
    allClasses() {
      const allClasses = [...this.classes];
      if (this.description) {
        allClasses.push(getModifierClass(this.baseClass, 'description'));
      }
      return allClasses;
    },
    description() {
      return this.links.some(({ description, descriptionOne }) => !!description || !!descriptionOne);
    },
  },
  methods: {
    getTrackingOptions({ text }) {
      return {
        position: this.pagePosition,
        rank: this.rank,
        title: text,
        type: TYPE_DEEP_LINK,
      };
    },
    onLinkClick(link) {
      trackClick(this.getTrackingOptions(link));
    },
  },
};
</script>

<style lang="scss" scoped>
$deep-links-gutter-width: $space-2l;
$deep-links-padding-bottom-mobile: $space-m;

.deep-links {
  @extend %no-scrollbar;

  margin: 0 -1 * $deep-links-padding-bottom-mobile -1 * $deep-links-padding-bottom-mobile;
  padding: $space-s $space-m $deep-links-padding-bottom-mobile;
  overflow-x: scroll;
  white-space: nowrap;

  @include desktop {
    overflow-x: hidden;
    white-space: normal;
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 0;
  }
}

.deep-links__item {
  display: inline-flex;
  margin-right: $space-1s;
  position: relative;

  @include desktop {
    margin-right: 0;

    &:not(:last-child)::after {
      content: '·';
      color: var(--color-text-secondary);
      margin-left: $space-1s;
      margin-right: $space-1s;
      font-weight: $font-weight-1l;
    }
  }
}

.deep-links__link-text {
  @extend %ellipsis;

  max-width: 32ch;

  @include tablet {
    max-width: none;
  }
}

.deep-links--description {
  @include desktop {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: $space-m;
    padding: $space-1s $space-m;
    margin: 0;
  }

  .deep-links__item {
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;

    // Desktop size is too wide for 50%,
    // leaving too much empty space.
    @include desktop {
      display: block;
      padding: $space-1s $space-m;
    }

    &:not(:last-child)::after {
      content: none;
    }
  }

  .deep-links__link {
    @include desktop {
      font-size: $font-l;
    }
  }

  .deep-links__description {
    @include line-clamp(2);

    margin: 0;
    display: none;

    @include desktop {
      display: -webkit-box;
    }
  }
}
</style>
